import './index.css';

import { configure } from '@nutrien/cxp-api-fetch-module';
import { Offline as OfflineIntegration } from '@sentry/integrations';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { Amplify, Analytics, AWSKinesisProvider } from 'aws-amplify';
import React from 'react';
import ReactDOM from 'react-dom';

import App from './components/App';
import ErrorBoundaryFallback from './components/ErrorBoundaryFallback';
import config from './config/appConfig';
import { MSTProvider, rootStore } from './mobx-models/Root';
import { RxDBHandler } from './rxdb/useRxDB';
import { isJestTest } from './test-helpers/isJestTest';
import { beforeSend } from './utilities/sentryFunctions';
import { OnlineStatusProvider } from './utilities/useOnlineStatus';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: `AIMS-Mobile@${import.meta.env.VITE_APP_VERSION}`,
  integrations: [
    new Integrations.BrowserTracing(),
    new OfflineIntegration({
      // limit how many events will be localled saved. Defaults to 30.
      maxStoredEvents: 50,
    }),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_SAMPLE_RATE || '0'),
  environment: import.meta.env.VITE_REACT_APP_ENV,
  sampleRate: parseFloat(import.meta.env.VITE_SENTRY_SAMPLE_RATE || '0'),
  replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE || '0'),
  autoSessionTracking: true,
  // Prevents messages from being truncated
  maxValueLength: 600,
  beforeSend,
});

Sentry.setContext('version', { version: import.meta.env.VITE_APP_VERSION || 'Unknown' });

if (!isJestTest()) {
  Analytics.addPluggable(new AWSKinesisProvider());
  Analytics.configure({
    AWSKinesis: {
      region: config.aws_project_region,
    },
  });
  Amplify.configure(config);
  configure({
    awsRegion: config.aws_cognito_region,
    cognitoRegion: config.aws_cognito_region,
    userPoolId: config.aws_user_pools_id,
    userPoolWebClientId: config.aws_user_pools_web_client_id,
  });
}

if (localStorage.getItem('DEBUG_AMPLIFY') !== null) Amplify.Logger.LOG_LEVEL = 'DEBUG';

(async () => {
  ReactDOM.render(
    <Sentry.ErrorBoundary fallback={ErrorBoundaryFallback}>
      <MSTProvider value={rootStore}>
        <OnlineStatusProvider>
          <RxDBHandler>
            <App />
          </RxDBHandler>
        </OnlineStatusProvider>
      </MSTProvider>
    </Sentry.ErrorBoundary>,
    document.getElementById('root'),
  );
})();
